import type { VisionItem } from '../types';
import { usePreviewTheme, withAlpha } from './PreviewTheme';

interface VisionSectionProps {
  items: VisionItem[];
}

export default function VisionSection({ items }: VisionSectionProps) {
  const theme = usePreviewTheme();

  if (!items || items.length === 0) {
    return null;
  }

  const displayItems = items.slice(0, 5);

  const hasContent = (item: VisionItem) => !!(item.time || item.goalTitle);

  const clampClass = 'overflow-hidden break-words [display:-webkit-box] [-webkit-box-orient:vertical]';

  return (
    <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="flex items-end gap-2 border-b border-gray-100 px-4 py-4">
        <h3 className="text-[18px] font-bold" style={{ color: theme.textHighlightColor }}>发展愿景</h3>
        <span className="pb-0.5 text-[12px] font-medium tracking-[0.28em] text-gray-400">OUR JOURNEY</span>
      </div>

      <div className="px-4 py-6">
        <div className="relative mx-auto max-w-[330px]">
          <div
            className="absolute left-1/2 w-1 -translate-x-1/2 rounded-full"
            style={{
              top: '50px',
              height: 'calc(100% - 80px)',
              background: `linear-gradient(180deg, ${withAlpha(theme.themeColor, '26')} 0%, ${withAlpha(theme.themeColor, '80')} 50%, ${theme.themeColor} 100%)`,
            }}
          />

          <div className="flex flex-col gap-5">
            {displayItems.map((item, idx) => {
              const isLeft = idx % 2 === 0;
              const nodeNum = idx + 1;
              const showCard = hasContent(item);

              return (
                <div key={item.id || idx} className="grid min-h-[84px] grid-cols-[minmax(0,1fr)_50px_minmax(0,1fr)] items-center">
                  <div className={isLeft ? 'pr-2 justify-self-end' : 'invisible pr-2 justify-self-end'}>
                    {showCard ? (
                      <div
                        className="relative w-[126px] rounded-[10px] bg-white px-3 py-3 text-right"
                        style={{ border: `1px solid ${withAlpha(theme.themeColor, '40')}`, boxShadow: `0 1px 0 ${withAlpha(theme.themeColor, '08')}` }}
                      >
                        <div className="absolute right-[-7px] top-1/2 h-0 w-0 -translate-y-1/2 border-y-[7px] border-y-transparent" style={{ borderLeft: `8px solid ${withAlpha(theme.themeColor, '40')}` }} />
                        {item.time && (
                          <div
                            className={`text-[13px] font-bold leading-[1.35] ${clampClass}`}
                            style={{ WebkitLineClamp: 2, color: theme.themeColor }}
                          >
                            {item.time}
                          </div>
                        )}
                        {item.goalTitle && (
                          <div
                            className={`mt-1 text-[11px] leading-[1.45] text-gray-800 ${clampClass}`}
                            style={{ WebkitLineClamp: 3 }}
                          >
                            {item.goalTitle}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="w-[126px]" />
                    )}
                  </div>

                  <div className="relative flex h-full items-center justify-center">
                    <div
                      className="relative z-10 flex h-10 w-10 items-center justify-center rounded-full border-[3px] border-white text-sm font-bold text-white"
                      style={{ background: theme.themeGradient, boxShadow: `0 4px 14px ${withAlpha(theme.themeColor, '59')}` }}
                    >
                      {nodeNum}
                    </div>
                  </div>

                  <div className={isLeft ? 'invisible pl-2 justify-self-start' : 'pl-2 justify-self-start'}>
                    {showCard ? (
                      <div
                        className="relative w-[126px] rounded-[10px] bg-white px-3 py-3 text-left"
                        style={{ border: `1px solid ${withAlpha(theme.themeColor, '40')}`, boxShadow: `0 1px 0 ${withAlpha(theme.themeColor, '08')}` }}
                      >
                        <div className="absolute left-[-7px] top-1/2 h-0 w-0 -translate-y-1/2 border-y-[7px] border-y-transparent" style={{ borderRight: `8px solid ${withAlpha(theme.themeColor, '40')}` }} />
                        {item.time && (
                          <div
                            className={`text-[13px] font-bold leading-[1.35] ${clampClass}`}
                            style={{ WebkitLineClamp: 2, color: theme.themeColor }}
                          >
                            {item.time}
                          </div>
                        )}
                        {item.goalTitle && (
                          <div
                            className={`mt-1 text-[11px] leading-[1.45] text-gray-800 ${clampClass}`}
                            style={{ WebkitLineClamp: 3 }}
                          >
                            {item.goalTitle}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="w-[126px]" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
